import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CourtGroup {
  id: string;
  organization_id: string;
  hall_id: string;
  name: string;
  display_name: string;
  description: string | null;
  pricing_category: string | null;
  sort_order: number;
  created_at: string;
  court_count?: number;
}

export function useCourtGroups(hallId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["court-groups", hallId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("court_groups" as any)
        .select("*, courts(count)")
        .eq("hall_id", hallId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []).map((g: any) => ({
        ...g,
        court_count: g.courts?.[0]?.count ?? 0,
      })) as CourtGroup[];
    },
    enabled: !!hallId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["court-groups"] });
    qc.invalidateQueries({ queryKey: ["admin-courts-list"] });
  };

  const createGroup = useMutation({
    mutationFn: async (values: { name: string; display_name: string; description?: string; pricing_category?: string; organization_id: string }) => {
      const maxSort = (query.data ?? []).reduce((m, g) => Math.max(m, g.sort_order), -1);
      const { error } = await supabase.from("court_groups" as any).insert({
        hall_id: hallId!,
        organization_id: values.organization_id,
        name: values.name.trim(),
        display_name: values.display_name.trim(),
        description: values.description?.trim() || null,
        pricing_category: values.pricing_category?.trim() || null,
        sort_order: maxSort + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Bankategori skapad"); },
    onError: (e: Error) => {
      if (e.message?.includes("duplicate")) toast.error("Kategorin finns redan");
      else toast.error("Kunde inte skapa kategori");
    },
  });

  const updateGroup = useMutation({
    mutationFn: async ({ id, ...values }: { id: string; name?: string; display_name?: string; description?: string | null; pricing_category?: string | null; sort_order?: number }) => {
      const payload: Record<string, unknown> = {};
      if (values.name !== undefined) payload.name = values.name.trim();
      if (values.display_name !== undefined) payload.display_name = values.display_name.trim();
      if (values.description !== undefined) payload.description = values.description?.trim() || null;
      if (values.pricing_category !== undefined) payload.pricing_category = values.pricing_category?.trim() || null;
      if (values.sort_order !== undefined) payload.sort_order = values.sort_order;
      const { error } = await supabase.from("court_groups" as any).update(payload).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Bankategori uppdaterad"); },
    onError: () => toast.error("Kunde inte uppdatera kategori"),
  });

  const deleteGroup = useMutation({
    mutationFn: async (id: string) => {
      // Unassign courts first
      await supabase.from("courts").update({ court_group_id: null } as any).eq("court_group_id", id);
      const { error } = await supabase.from("court_groups" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Bankategori borttagen"); },
    onError: () => toast.error("Kunde inte ta bort kategori"),
  });

  const assignCourtToGroup = useMutation({
    mutationFn: async ({ courtId, groupId }: { courtId: string; groupId: string | null }) => {
      const { error } = await supabase.from("courts").update({ court_group_id: groupId } as any).eq("id", courtId);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); },
    onError: () => toast.error("Kunde inte tilldela bana"),
  });

  return {
    groups: query.data ?? [],
    isLoading: query.isLoading,
    createGroup,
    updateGroup,
    deleteGroup,
    assignCourtToGroup,
  };
}
